/**
 * longHairWarning: claude copied from our builder's react component to regular js to use in non-react contexts :) works well but looks kinda long
 *
 * Progressively enhances `.long-hair-warning` snippet elements (non-React version) with
 * the same cursor-following tooltip behaviour used in the React builder.
 *
 * - Fine pointer (mouse): tooltip follows cursor while hovered.
 * - Coarse pointer (touch): tap triggers fixed tooltip anchored near the trigger; tap outside closes.
 * - Keyboard: focus opens tooltip anchored to trigger position; Escape closes.
 * - Accessible: trigger gets aria-describedby + aria-expanded while tooltip is visible.
 *
 * Skips any element that already has `.js-long-hair-warning` (React-managed instances).
 */

const WARNING_ICON_URL =
    'https://cdn.shopify.com/s/files/1/0223/5446/0736/files/exclamationmark-yellow.png?v=1774455631';

let instanceCount = 0;

function getIsCoarsePointer() {
    const mqCoarse = window.matchMedia('(pointer: coarse)').matches;
    const touchCapable = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    return mqCoarse || touchCapable;
}

function clampToViewport(x, y, tooltipEl) {
    const margin = 8;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    // Use measured dimensions if available (tooltip already visible), otherwise assume defaults.
    const width = tooltipEl && tooltipEl.offsetWidth ? tooltipEl.offsetWidth : 240;
    const height = tooltipEl && tooltipEl.offsetHeight ? tooltipEl.offsetHeight : 100;
    let nx = x;
    let ny = y;
    if (nx + width + margin > vw) nx = vw - width - margin;
    if (ny + height + margin > vh) ny = vh - height - margin;
    if (nx < margin) nx = margin;
    if (ny < margin) ny = margin;
    return { x: nx, y: ny };
}

function initWarning(container) {
    // Extract content from the existing <div> child before we rewrite the DOM.
    const contentDiv = container.querySelector(':scope > div');
    if (!contentDiv) return;
    const innerHtml = contentDiv.innerHTML.trim();
    if (!innerHtml) return;

    const id = `long-hair-warning-snippet-${++instanceCount}`;
    const coarse = getIsCoarsePointer();

    // ── Build trigger button ──────────────────────────────────────────────────
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'long-hair-warning__trigger';
    button.setAttribute('aria-label', 'Product warning');

    const img = document.createElement('img');
    img.src = WARNING_ICON_URL;
    img.alt = '';
    img.setAttribute('aria-hidden', 'true');
    button.appendChild(img);

    // ── Rewrite DOM ───────────────────────────────────────────────────────────
    const existingSpan = container.querySelector(':scope > span');
    if (existingSpan) existingSpan.remove();
    contentDiv.remove();
    container.appendChild(button);
    container.classList.add('js-long-hair-warning');
    container.classList.add('js-long-hair-warning-snippet');

    // ── Create tooltip (appended to <body> to escape stacking contexts) ───────
    const tooltip = document.createElement('div');
    tooltip.id = id;
    tooltip.className = 'long-hair-warning__tooltip';
    tooltip.setAttribute('role', 'tooltip');
    tooltip.innerHTML = innerHtml;
    tooltip.style.cssText = 'display:none; left:0; top:0;';
    document.body.appendChild(tooltip);

    // ── State ─────────────────────────────────────────────────────────────────
    let visible = false;
    let fixedAt = null;
    // let mouseMoved = false;
    // let lastOpenWasKeyboard = false;
    let coords = { x: 0, y: 0 };

    function setCoords(x, y) {
        coords = { x, y };
        tooltip.style.left = `${x}px`;
        tooltip.style.top = `${y}px`;
    }

    function repositionFromTrigger() {
        const rect = button.getBoundingClientRect();
        const clamped = clampToViewport(rect.left + rect.width / 2 + 12, rect.top + rect.height / 2 + 12, tooltip);
        fixedAt = clamped;
        setCoords(clamped.x, clamped.y);
    }

    // ── Listener callbacks (defined once, reused for add/remove) ─────────────

    const onMouseMove = (e) => {
        if (coarse || fixedAt) return;
        // mouseMoved = true;
        const clamped = clampToViewport(e.clientX + 12, e.clientY + 12, tooltip);
        setCoords(clamped.x, clamped.y);
    };

    const onResizeScroll = () => {
        const clamped = clampToViewport(coords.x, coords.y, tooltip);
        setCoords(clamped.x, clamped.y);
    };

    const onKeydown = (e) => {
        if (e.key === 'Escape') hide(); // eslint-disable-line no-use-before-define
    };

    const onPointerDownOutside = (e) => {
        if (!button.contains(e.target) && !tooltip.contains(e.target)) {
            hide(); // eslint-disable-line no-use-before-define
        }
    };

    function attachVisibleListeners() {
        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('resize', onResizeScroll);
        window.addEventListener('scroll', onResizeScroll, true);
        window.addEventListener('keydown', onKeydown);
        if (coarse) {
            window.addEventListener('pointerdown', onPointerDownOutside, true);
        }
    }

    function detachVisibleListeners() {
        window.removeEventListener('mousemove', onMouseMove);
        window.removeEventListener('resize', onResizeScroll);
        window.removeEventListener('scroll', onResizeScroll, true);
        window.removeEventListener('keydown', onKeydown);
        if (coarse) {
            window.removeEventListener('pointerdown', onPointerDownOutside, true);
        }
    }

    // ── Show / Hide ───────────────────────────────────────────────────────────

    function show(reason, evt) {
        if (visible) return;
        visible = true;
        // mouseMoved = false;
        // lastOpenWasKeyboard = reason === 'keyboard';

        tooltip.style.display = '';
        button.setAttribute('aria-describedby', id);
        button.setAttribute('aria-expanded', 'true');

        if (coarse || reason === 'keyboard') {
            // Use rAF so the tooltip has dimensions before we measure it.
            requestAnimationFrame(repositionFromTrigger);
        } else if (reason === 'pointer' && evt) {
            // Seed position at pointer — onMouseMove will refine on next move.
            const clamped = clampToViewport(evt.clientX + 12, evt.clientY + 12, null);
            setCoords(clamped.x, clamped.y);
        }

        attachVisibleListeners();
    }

    function hide() {
        if (!visible) return;
        visible = false;
        fixedAt = null;
        // lastOpenWasKeyboard = false;
        // mouseMoved = false;

        tooltip.style.display = 'none';
        button.removeAttribute('aria-describedby');
        button.removeAttribute('aria-expanded');

        detachVisibleListeners();
    }

    // ── Trigger event bindings ────────────────────────────────────────────────

    if (!coarse) {
        button.addEventListener('mouseenter', (e) => show('pointer', e));
        button.addEventListener('mouseleave', hide);
        button.addEventListener('focus', () => show('keyboard'));
    } else {
        // Tap toggles on coarse devices; prevent click/pointerup from bubbling a duplicate.
        button.addEventListener('pointerdown', (e) => {
            e.preventDefault();
            if (visible) hide();
            else show('coarse');
        });
        button.addEventListener('pointerup', (e) => e.preventDefault());
        button.addEventListener('click', (e) => e.preventDefault());
    }

    button.addEventListener('blur', hide);
}

const longHairWarning = () => {
    document.addEventListener('DOMContentLoaded', () => {
        document.querySelectorAll('.long-hair-warning:not(.js-long-hair-warning)').forEach(initWarning);
    });
};

export default longHairWarning;
